/**
 * @file: util.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

/* eslint-disable @typescript-eslint/no-use-before-define */
/**
 * @file: SiteInspectionManager.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { MediaInfo } from '@euler/app/flows/task/functions/models';
import {
  isComplexInspectionResultDataType,
  isSimpleNumericInspectionResultDataType,
  isSimpleStringInspectionResultDataType,
} from '@euler/functions/inspectionResultDataTypeHelpers';
import {
  AnyMedia,
  InspectionFlowSummary,
  InspectionItemInfo,
  InspectionItemOptionInfo,
  InspectionSiteInfo,
} from '@euler/model';
import {
  AbnormalLevel,
  DeliveryCheckSceneType,
  DeliveryCheckTemplateSceneType,
  OptionValueType,
  SeverityLevel,
  SiteInspectionType,
} from '@euler/model/enum';
import {
  AnyStagedMedia,
  ConstructionJob,
  ConstructionJobContextDataStaged,
  ConstructionJobMedia,
  ConstructionJobMediaStaged,
  ConstructionJobStaged,
  CustomIssue,
  CustomIssueItemStaged,
  DeliveryCheckItemStagedOption,
  SiteCustomIssue,
  SiteCustomIssueCommitPayload,
  SiteInspection,
  SiteInspectionCommitPayload,
  SiteInspectionItem,
  SiteInspectionItemCommitPayload,
  SiteInspectionItemComplexResult,
  SiteInspectionItemNumericResult,
  SiteInspectionItemResult,
  SiteInspectionItemStaged,
  SiteInspectionItemStringResult,
  SiteInspectionStaged,
  TaskMediaCommitPayload,
  TaskMediaStaged,
  kConstructionJobContextVersion,
} from '@euler/model/task-detail';
import { comparer, isNotNull } from '@euler/utils';
import { array2map, sorted } from '@euler/utils/array';
import { dateFromValue } from '@euler/utils/datetime';
import { nanoid } from 'nanoid/non-secure';
import { clone } from 'ramda';

export const deliveryCheckTemplateSceneTypeToDeliveryCheckSceneType = (
  templateSceneType: DeliveryCheckTemplateSceneType,
): DeliveryCheckSceneType => {
  return templateSceneType === DeliveryCheckTemplateSceneType.CompletionCheck
    ? DeliveryCheckSceneType.CompletionCheck
    : DeliveryCheckSceneType.SamplingCheck;
};

export const siteInspectionToStaged = (
  flowId: string,
  site: InspectionSiteInfo,
  inspectionType: SiteInspectionType,
  siteInspection: SiteInspection | undefined,
  technicianId: number,
  technicianName: string,
): SiteInspectionStaged => {
  const inspectedItemMapByItemId = array2map(
    siteInspection?.items ?? [],
    x => x.itemId,
  );
  const items = site.checkItems.map(checkItem => {
    let customLabelOptionIndex: number | undefined = undefined;
    const inspectedItem = inspectedItemMapByItemId.get(checkItem.id);
    if (inspectedItem) {
      if (inspectedItem.isCustom) {
        // has custom label, find target option by defective level
        // has custom defective level or maintenance advice.
        // it's impossible to find the one that is modified there're
        // multiple matches.
        const sortedOptions = sorted(checkItem.options, compareOption);
        let options = sortedOptions.filter(
          x =>
            x.abnormalLevel === inspectedItem.abnormalLevel &&
            x.severityLevel === inspectedItem.severityLevel,
        );
        __DEV__ &&
          console.log(
            'identifying target custom option with label:',
            inspectedItem.label ?? inspectedItem.optionLabel,
            ', of item:',
            inspectedItem.name,
          );

        __DEV__ &&
          console.log(
            '--------------------------------------------------------',
          );

        if (customLabelOptionIndex == null) {
          if (inspectedItem.label != null) {
            options = sortedOptions.filter(
              x =>
                x.label === (inspectedItem.label ?? inspectedItem.optionLabel),
            );
            __DEV__ &&
              console.log(
                `#1: ${options.length} matches found by custom label:`,
                inspectedItem.label,
                ', from option labels: ',
                JSON.stringify(options.map(x => x.label)),
              );
            if (options.length === 1) {
              customLabelOptionIndex = sortedOptions.indexOf(options[0]);
              __DEV__ && console.log(`  #1.1: found exact one match by label`);
            } else {
              __DEV__ &&
                console.log(`  #1.2: no match or multiple matches, skipped`);
            }
          }
        }

        if (customLabelOptionIndex == null) {
          if (inspectedItem.optionLabel != null) {
            options = sortedOptions.filter(
              x => x.label === inspectedItem.optionLabel,
            );
            __DEV__ &&
              console.log(
                `#2: ${options.length} matches found by option label:`,
                inspectedItem.optionLabel,
                ', from option labels: ',
                JSON.stringify(options.map(x => x.label)),
              );
            if (options.length === 1) {
              customLabelOptionIndex = sortedOptions.indexOf(options[0]);
              __DEV__ && console.log(`  #2.1: found exact one match by label`);
            } else {
              __DEV__ &&
                console.log(`  #2.2: no match or multiple matches, skipped`);
            }
          }
        }

        if (customLabelOptionIndex == null) {
          __DEV__ &&
            console.log(
              '#3. exact matches of defective levels:',
              options.length,
            );

          if (options.length === 1) {
            customLabelOptionIndex = sortedOptions.indexOf(options[0]);
            __DEV__ &&
              console.log(
                '  #3.1 found exact match at index',
                customLabelOptionIndex,
              );
          } else if (options.length === 0) {
            __DEV__ && console.log('  #3.2 no exact matches found');
            options = sortedOptions.filter(
              x => x.abnormalLevel === inspectedItem.abnormalLevel,
            );
            __DEV__ &&
              console.log('#4 matches by abnormal level:', options.length);
            if (options.length === 1) {
              customLabelOptionIndex = sortedOptions.indexOf(options[0]);
              __DEV__ &&
                console.log(
                  '  #4.1 exact one match at index',
                  customLabelOptionIndex,
                );
            } else {
              __DEV__ &&
                console.log('  #4.2 no match or multiple matches, skipped');
              options = sortedOptions.filter(
                x => x.severityLevel === inspectedItem.severityLevel,
              );
              __DEV__ &&
                console.log('#5 matches by severity level:', options.length);
              if (options.length === 1) {
                customLabelOptionIndex = sortedOptions.indexOf(options[0]);
                __DEV__ &&
                  console.log(
                    '  #5.1 exact one match at index',
                    customLabelOptionIndex,
                  );
              } else {
                __DEV__ &&
                  console.log('  #5.2 no match or multiple matches, skipped');
              }
            }
          } else {
            __DEV__ && console.log('  #3.3 multiple matches found');
          }
        }

        __DEV__ && console.log('\n');
      }
      const itemStaged = siteInspectionItemToStaged(inspectedItem);
      itemStaged.customLabelOptionIndex = customLabelOptionIndex;
      return itemStaged;
    }
    return makeInitialInspectedItemStaged(checkItem);
  });
  if (siteInspection?.customIssues?.length) {
    for (const customIssue of siteInspection.customIssues) {
      items.push(siteCustomIssueToStaged(customIssue));
    }
  }

  return {
    id: siteInspection?.id,
    flowId,
    siteId: siteInspection?.siteId ?? site.id,
    siteName: site.name,
    siteCode: site.code,
    commitId: siteInspection?.commitId,
    inspectionType: siteInspection?.inspectionType ?? inspectionType,
    technicianId: siteInspection?.technicianId ?? technicianId,
    technicianName: siteInspection?.technicianName ?? technicianName,
    abnormalLevel: siteInspection?.abnormalLevel ?? AbnormalLevel.Fine,
    severityLevel: siteInspection?.severityLevel ?? SeverityLevel.None,
    extraData: siteInspection?.extraData,
    remark: siteInspection?.remark,
    items,
  };
};

export const makeInitialInspectedItemStaged = (
  item: InspectionItemInfo,
): SiteInspectionItemStaged => {
  // eslint-disable-next-line @typescript-eslint/init-declarations
  let result: SiteInspectionItemResult;
  if (
    item.valueType != null &&
    isSimpleNumericInspectionResultDataType(item.valueType)
  ) {
    result = {
      valueType: item.valueType,
      unit: item.valueUnit ?? undefined,
    };
  } else if (
    item.valueType != null &&
    isComplexInspectionResultDataType(item.valueType)
  ) {
    result = {
      valueType: item.valueType,
      data: '',
    };
  } else {
    result = {
      valueType: OptionValueType.String,
      value: '',
    };
  }
  return {
    _key: nanoid(),
    type: 'item',
    itemId: item.id,
    name: item.name,
    abnormalLevel: AbnormalLevel.Fine,
    severityLevel: SeverityLevel.None,
    isCustom: false,
    result,
    medias: [],
  };
};

export const anyMediaToMediaStaged = <T extends AnyMedia>(
  media: T,
): TaskMediaStaged => {
  return {
    _key: nanoid(),
    id: media.id,
    type: media.type,
    coverUrl: media.coverUrl,
    title: media.title,
    subTitle: media.subTitle,
    remark: media.remark,
    annotationMetadata: media.annotationMetadata
      ? JSON.parse(media.annotationMetadata)
      : undefined,
    _source: { type: 'remote', url: media.url, cid: media.cid },
    _timestamp: dateFromValue(media.createdAt).getTime(),
  };
};

export const siteInspectionItemToStaged = (
  item: SiteInspectionItem,
): SiteInspectionItemStaged & { type: 'item' } => {
  // eslint-disable-next-line @typescript-eslint/init-declarations
  let result: SiteInspectionItemResult;
  if (isSimpleNumericInspectionResultDataType(item.resultDataType)) {
    result = {
      valueType: item.resultDataType,
      value: item.resultDataNumberValue,
      unit: item.resultDataUnit,
      range: {
        lower: item.optionLower,
        upper: item.optionUpper,
        lowerInclusive: item.optionLowerInclusive,
        upperInclusive: item.optionUpperInclusive,
      },
    } as SiteInspectionItemNumericResult;
  } else if (isSimpleStringInspectionResultDataType(item.resultDataType)) {
    result = {
      valueType: item.resultDataType,
      value: item.resultDataStringValue,
    } as SiteInspectionItemStringResult;
  } else {
    result = {
      valueType: item.resultDataType,
      data: '', // not implemented.
    } as SiteInspectionItemComplexResult;
  }
  return {
    _key: nanoid(),
    type: 'item',
    id: item.id,
    name: item.name,
    label: item.label,
    remark: item.remark,
    abnormalLevel: item.abnormalLevel,
    severityLevel: item.severityLevel,

    itemId: item.itemId,
    optionLabel: item.optionLabel,
    optionLabelFormat: item.optionLabelFormat,
    maintenanceAdvice: item.maintenanceAdvice,
    description: item.resultDescription,
    constructionJobId: item.constructionJobId,
    isCustom: item.isCustom,
    result,

    medias: item.medias.map(anyMediaToMediaStaged),
  };
};

export const siteCustomIssueToStaged = (
  customIssue: SiteCustomIssue,
): SiteInspectionItemStaged & { type: 'customIssue' } => {
  return {
    _key: nanoid(),
    id: customIssue.id,
    type: 'customIssue',
    name: customIssue.itemName,
    label: customIssue.label,
    remark: customIssue.description,
    abnormalLevel: customIssue.abnormalLevel,
    severityLevel: customIssue.severityLevel,
    defectiveClass: customIssue.defectiveClass,
    inspectionType: customIssue.inspectionType,
    siteId: customIssue.siteId,
    siteName: customIssue.siteName,
    itemId: customIssue.itemId,
    itemName: customIssue.itemName,
    referenceState: customIssue.referenceState,
    inspectionResult: customIssue.inspectionResult,
    maintenanceAdvice: customIssue.maintenanceAdvice,
    siteInfluenceFactor: customIssue.siteInfluenceFactor,
    itemInfluenceFactor: customIssue.itemInfluenceFactor,
    technicianId: customIssue.technicianId,
    technicianName: customIssue.technicianName,

    medias: customIssue.medias.map(anyMediaToMediaStaged),
  };
};

export const customIssueToStaged = (
  customIssue: CustomIssue,
): CustomIssueItemStaged => {
  return {
    _key: nanoid(),
    id: customIssue.id,
    siteId: customIssue.siteId,
    siteName: customIssue.siteName,
    itemId: customIssue.itemId,
    itemName: customIssue.itemName,
    inspectionType: customIssue.inspectionType,
    label: customIssue.label,
    description: customIssue.description,
    abnormalLevel: customIssue.abnormalLevel,
    severityLevel: customIssue.severityLevel,
    defectiveClass: customIssue.defectiveClass,
    referenceState: customIssue.referenceState,
    inspectionResult: customIssue.inspectionResult,
    maintenanceAdvice: customIssue.maintenanceAdvice,
    siteInfluenceFactor: customIssue.siteInfluenceFactor,
    itemInfluenceFactor: customIssue.itemInfluenceFactor,

    medias: customIssue.medias.map(anyMediaToMediaStaged),
    relatedFaultLikeSubjects: undefined,
  };
};

export const customIssueStagedToItemStaged = (
  customIssue: CustomIssueItemStaged,
  technicianId: number,
  technicianName: string,
): SiteInspectionItemStaged => {
  return {
    _key: customIssue._key,
    id: customIssue.id,
    type: 'customIssue',
    name:
      customIssue.siteId != null
        ? customIssue.itemName!
        : customIssue.siteName!,
    label: customIssue.label,
    remark: customIssue.description,
    abnormalLevel: customIssue.abnormalLevel!,
    severityLevel: customIssue.severityLevel!,
    inspectionType: customIssue.inspectionType,
    referenceState: customIssue.referenceState,
    inspectionResult: customIssue.inspectionResult!,
    maintenanceAdvice: customIssue.maintenanceAdvice!,
    siteInfluenceFactor: customIssue.siteInfluenceFactor,
    itemInfluenceFactor: customIssue.itemInfluenceFactor,
    technicianId,
    technicianName,

    // deep copy instead of assign reference
    medias: clone(customIssue.medias),
    relatedFaultLikeSubjects: customIssue.relatedFaultLikeSubjects,
  };
};

export const inspectedItemStagedToCustomIssueStaged = (
  item: SiteInspectionItemStaged,
): CustomIssueItemStaged | undefined => {
  if (item.type !== 'customIssue') return undefined;
  return {
    _key: item._key,
    id: item.id,
    siteId: item.siteId,
    siteName: item.siteName,
    itemId: item.itemId,
    itemName: item.itemName,
    inspectionType: item.inspectionType,
    label: item.label,
    description: item.remark,
    abnormalLevel: item.abnormalLevel,
    severityLevel: item.severityLevel,

    referenceState: item.referenceState,
    inspectionResult: item.inspectionResult,
    maintenanceAdvice: item.maintenanceAdvice,
    siteInfluenceFactor: item.siteInfluenceFactor,
    itemInfluenceFactor: item.itemInfluenceFactor,

    // deep copy instead of assign reference
    medias: clone(item.medias),

    relatedFaultLikeSubjects: item.relatedFaultLikeSubjects,
  };
};

export const mediaStagedToCommitPayload = (
  media: TaskMediaStaged,
): TaskMediaCommitPayload | null => {
  let url = '';
  if (media._source.type === 'local') {
    if (media._source.state.status === 'uploaded') {
      url = media._source.state.result.url;
    } else {
      return null;
    }
  } else {
    url = media._source.url;
  }
  return {
    id: media.id,
    type: media.type === 'video/mov' ? 'video/mp4' : media.type,
    url,
    coverUrl: media.coverUrl,
    title: media.title,
    subTitle: media.subTitle,
    remark: media.remark,
    annotationMetadata: media.annotationMetadata
      ? JSON.stringify(media.annotationMetadata)
      : undefined,
  };
};

export const isMediaStagedPending = (media: AnyStagedMedia) => {
  return (
    media._source.type === 'local' && media._source.state.status !== 'uploaded'
  );
};

export type AnyInspectionItem = {
  abnormalLevel?: AbnormalLevel;
  severityLevel?: SeverityLevel;
};

export const collectInspectionFlowSummary = <T extends AnyInspectionItem>(
  summary: InspectionFlowSummary,
  items: T[],
) => {
  if (items.some(x => x.severityLevel === SeverityLevel.Danger)) {
    summary.urgentCount++;
  } else if (items.some(x => x.severityLevel === SeverityLevel.Warning)) {
    summary.warningCount++;
  }
  if (
    items.some(
      x =>
        x.severityLevel === SeverityLevel.Notice ||
        x.severityLevel === SeverityLevel.Slight,
    )
  ) {
    summary.defectiveCount++;
  } else {
    summary.normalCount++;
  }
};

export const buildInspectionFlowSummary = (
  builder: (summary: InspectionFlowSummary) => void,
) => {
  const summary: InspectionFlowSummary = {
    normalCount: 0,
    urgentCount: 0,
    warningCount: 0,
    defectiveCount: 0,
  };
  builder(summary);
  return summary;
};

export const getSummaryOfSiteInspections = <
  T extends SiteInspectionStaged,
  U extends CustomIssueItemStaged,
>(
  siteInspections: T[],
  customIssues: U[] | undefined,
  siteInspectionPredict?: (siteInspection: T) => boolean,
  customIssuePredict?: (customIssue: U) => boolean,
) => {
  return buildInspectionFlowSummary(summary => {
    siteInspections
      .filter(x => !siteInspectionPredict || siteInspectionPredict(x))
      .forEach(siteInspection => {
        collectInspectionFlowSummary(summary, siteInspection.items);
      });
    if (customIssues != null) {
      customIssues
        .filter(x => !customIssuePredict || customIssuePredict(x))
        .forEach(customIssue => {
          collectInspectionFlowSummary(summary, [customIssue]);
        });
    }
  });
};

export const mediaStagedToMediaInfo = (media: TaskMediaStaged): MediaInfo => {
  return {
    id: media.id,
    cid: media._source.type === 'remote' ? media._source.cid : undefined,
    key: media._key,
    type: media.type.includes('video') ? 'video' : 'image',
    url:
      media._source.type === 'remote'
        ? media._source.url
        : media._source.state.status === 'uploaded'
        ? media._source.state.result.url
        : media._source.localFileUri,
    coverUrl: media.coverUrl,
    description: media.remark ?? undefined,
    status:
      media._source.type === 'local' ? media._source.state.status : undefined,
    source: media._source,
    progress:
      media._source.type === 'local' &&
      (media._source.state.status === 'uploading' ||
        media._source.state.status === 'compressing')
        ? media._source.state.progress
        : undefined,
    error:
      media._source.type === 'local' && media._source.state.status === 'error'
        ? media._source.state.error
        : undefined,
  };
};

export const compareOption = (
  x: InspectionItemOptionInfo,
  y: InspectionItemOptionInfo,
): number => {
  if (x.abnormalLevel === y.abnormalLevel) {
    return comparer(x.severityLevel, y.severityLevel);
  }
  return comparer(x.abnormalLevel, y.abnormalLevel);
};

export const constructionJobMediaToStaged = (
  media: ConstructionJobMedia,
  fromInspectionResult?: boolean,
): ConstructionJobMediaStaged => {
  return {
    ...anyMediaToMediaStaged(media),
    fromInspectionResult,
    detailId: media.detailId,
    tag: media.tag,
  };
};

export const constructionJobToStaged = (
  job: ConstructionJob,
  params: {
    technicianId: number;
    technicianName: string;
  },
): ConstructionJobStaged => {
  const context: ConstructionJobContextDataStaged = job.context
    ? {
        version: job.context.version,
        category: job.context.category,
        procedures: job.context.procedures.map(x => ({
          name: x.name,
          type: x.type ?? 'custom',
          removable:
            x.removable ??
            job.context?.defaultProcedures?.find(p => p.name === x.name)
              ?.removable,
          allowChooseMediaFromGallery:
            x.allowChooseMediaFromGallery ??
            job.context?.defaultProcedures?.find(p => p.name === x.name)
              ?.allowChooseMediaFromGallery,
        })),
        defaultProcedures: job.context.defaultProcedures,
        productConfirmation: job.context.productConfirmation,
        resolved: job.context.resolved,
        saved: job.context.saved,
      }
    : {
        version: kConstructionJobContextVersion,
        procedures: job.procedures.map(x => ({
          name: x.name,
          type: x.type ?? 'custom',
          removable: x.removable,
          allowChooseMediaFromGallery: x.allowChooseMediaFromGallery,
        })),
      };
  return {
    id: job.id,
    name: job.name,
    commitId: job.commitId,
    remark: job.remark,
    technicianId: job.technicianId ?? params.technicianId,
    technicianName: job.technicianName ?? params.technicianName,
    context,
    procedures: job.procedures.map(procedure => ({
      name: procedure.name,
      type: procedure.type,
      medias: procedure.medias.map(media =>
        constructionJobMediaToStaged(media),
      ),
      removable: job.context?.defaultProcedures?.find(
        x => x.name === procedure.name,
      )?.removable,
      allowChooseMediaFromGallery: job.context?.defaultProcedures?.find(
        x => x.name === procedure.name,
      )?.allowChooseMediaFromGallery,
    })),
  };
};

export const defaultDeliveryCheckItemStagedOptions = (
  constructionJobId?: number,
): DeliveryCheckItemStagedOption[] => {
  if (constructionJobId == null) {
    return [
      {
        title: '是',
        isDefaultChecked: false,
      },
      { title: '否', isDefaultChecked: false },
    ];
  }
  return [
    {
      title: '检查合格',
      isDefaultChecked: true,
      isExpected: true,
    },
    {
      title: '不合格',
      isDefaultChecked: false,
    },
  ];
};

export const siteInspectionStagedToCommitPayload = (
  staged: SiteInspectionStaged,
  params: {
    flowId: string;
    inspectionType: SiteInspectionType;
    siteId: number;
    siteName: string;
    technicianId: number;
    technicianName: string;
  },
): SiteInspectionCommitPayload => {
  let abnormalLevel = AbnormalLevel.Fine;
  let severityLevel = SeverityLevel.None;
  const items: SiteInspectionItemCommitPayload[] = [];
  const customIssues: SiteCustomIssueCommitPayload[] = [];
  for (const stagedItem of staged.items) {
    if (
      stagedItem.abnormalLevel > abnormalLevel ||
      stagedItem.severityLevel > severityLevel
    ) {
      abnormalLevel = stagedItem.abnormalLevel;
      severityLevel = stagedItem.severityLevel;
    }
    if (stagedItem.type === 'item') {
      items.push({
        id: stagedItem.id,
        itemId: stagedItem.itemId,
        name: stagedItem.name,
        label: stagedItem.label,
        abnormalLevel: stagedItem.abnormalLevel,
        severityLevel: stagedItem.severityLevel,
        description: stagedItem.description,
        remark: stagedItem.remark,
        optionLabel: stagedItem.optionLabel,
        optionLabelFormat: stagedItem.optionLabelFormat,
        maintenanceAdvice: stagedItem.maintenanceAdvice,
        constructionJobId: stagedItem.constructionJobId,
        isCustom: stagedItem.isCustom,
        result: stagedItem.result,
        medias: stagedItem.medias
          .map(mediaStagedToCommitPayload)
          .filter(isNotNull),
        relatedFaultLikeSubjects: stagedItem.relatedFaultLikeSubjects
          ?.filter(x => x.checked)
          .map(x => x.name),
      });
    } else {
      // custom issue.
      customIssues.push({
        id: stagedItem.id,
        flowId: params.flowId,
        inspectionType: params.inspectionType,
        siteId: params.siteId,
        siteName: params.siteName,
        itemId: undefined,
        itemName: '自定义',
        abnormalLevel: stagedItem.abnormalLevel,
        severityLevel: stagedItem.severityLevel,
        technicianId: params.technicianId,
        technicianName: params.technicianName,
        siteInfluenceFactor: stagedItem.siteInfluenceFactor,
        itemInfluenceFactor: stagedItem.itemInfluenceFactor,
        inspectionResult: stagedItem.inspectionResult,
        referenceState: stagedItem.referenceState,
        maintenanceAdvice: stagedItem.maintenanceAdvice,
        description: stagedItem.remark,
        medias: stagedItem.medias
          .map(mediaStagedToCommitPayload)
          .filter(isNotNull),
        relatedFaultLikeSubjects: stagedItem.relatedFaultLikeSubjects
          ?.filter(x => x.checked)
          .map(x => x.name),
      });
    }
  }
  const payload: SiteInspectionCommitPayload = {
    id: staged.id,
    flowId: params.flowId,
    siteId: params.siteId,
    commitId: staged.commitId,
    inspectionType: params.inspectionType,
    technicianId: params.technicianId,
    technicianName: params.technicianName,
    abnormalLevel,
    severityLevel,
    extraData: staged.extraData,
    remark: staged.remark,
    items,
    customIssues,
  };

  return payload;
};
